import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBookOpen, FaCheckCircle, FaCalendar, FaClipboardList, FaInfoCircle, FaCircle } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import RichTextRenderer from '../common/RichTextRenderer';
import { getCourseContent, isContentCompleted } from '../../utils/progressUtils';

const easing = [0.22, 1, 0.36, 1];

const CourseDetailModal = ({
  selectedCourse,
  onClose,
  userProgress,
  userCurrentContent,
  userCurrentCourse,
  courseProgress,
  isCourseCompletedLocal,
  expandedLessons,
  setExpandedLessons,
  toggleLessonAccordion,
  onLessonClick
}) => {
  const [descExpanded, setDescExpanded] = useState(false);

  React.useEffect(() => {
    setDescExpanded(false);
  }, [selectedCourse?._id]);

  const isEventOrInfo = selectedCourse && (
    selectedCourse.type === 'event' ||
    selectedCourse.type === 'information' ||
    selectedCourse.date
  );

  const handleClose = () => {
    setExpandedLessons(new Set());
    onClose();
  };

  // Status copy
  const statusLabel = (() => {
    if (!selectedCourse) return null;
    if (selectedCourse.type === 'information') return { eyebrow: 'Information', detail: null };
    if (selectedCourse.type === 'event' || selectedCourse.date) return { eyebrow: 'Event', detail: null };
    if (isCourseCompletedLocal(selectedCourse._id)) return { eyebrow: 'Completed', detail: null };
    if (userCurrentCourse?._id === selectedCourse._id) return { eyebrow: 'In Progress', detail: `${courseProgress}%` };
    return { eyebrow: 'Upcoming', detail: null };
  })();

  return ReactDOM.createPortal(
    <AnimatePresence>
      {selectedCourse && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center z-[9999] p-3 sm:p-6"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: easing }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            className="bg-background w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
            style={{ borderRadius: '4px', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.4)' }}
            initial={{ opacity: 0, y: 24, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.985 }}
            transition={{ duration: 0.45, ease: easing }}
          >
            {/* Hero — kept editorial: thumbnail (or primary band) with a single status stamp */}
            <div className="flex-shrink-0 relative">
              <div className="w-full aspect-[16/9] bg-secondary overflow-hidden relative">
                {selectedCourse.thumbnail && (selectedCourse.thumbnail.url || selectedCourse.thumbnail.data) ? (
                  <motion.img
                    src={selectedCourse.thumbnail.url || `data:${selectedCourse.thumbnail.contentType};base64,${selectedCourse.thumbnail.data}`}
                    alt={selectedCourse.title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.04 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.2, ease: easing }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <FaBookOpen className="w-10 h-10 text-primary/30" />
                  </div>
                )}
                {/* Subtle bottom fade so the stamp band reads */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
              </div>

              {/* Status eyebrow over the bottom edge */}
              {statusLabel && (
                <motion.div
                  className="absolute left-5 sm:left-7 bottom-4 flex items-center gap-2.5"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2, ease: easing }}
                >
                  <span className="inline-block h-px w-5 bg-white/80" aria-hidden="true" />
                  <span className="text-[10px] font-bold text-white tracking-[0.22em] uppercase">
                    {statusLabel.eyebrow}
                    {statusLabel.detail && (
                      <span className="ml-2 text-white/70 tabular-nums">{statusLabel.detail}</span>
                    )}
                  </span>
                </motion.div>
              )}

              {/* Close — restrained */}
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-background/90 backdrop-blur-sm text-foreground/70 hover:text-foreground hover:bg-background flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="px-5 sm:px-8 pt-6 sm:pt-7 pb-6 sm:pb-8">

                {/* Title block */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15, ease: easing }}
                >
                  <h3
                    className="font-display font-bold text-foreground leading-[1.15]"
                    style={{ fontSize: 'clamp(22px, 3vw, 32px)', letterSpacing: '-0.02em' }}
                  >
                    {selectedCourse.title}
                  </h3>
                </motion.div>

                {/* Description */}
                {selectedCourse.description && (
                  <motion.div
                    className="mt-5"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25, ease: easing }}
                  >
                    <div className={!descExpanded ? 'line-clamp-3 overflow-hidden' : ''}>
                      <RichTextRenderer
                        content={selectedCourse.description}
                        className="modal-description"
                        fontSize="15px"
                        lineHeight="1.6"
                        allowInlineStyles={true}
                      />
                    </div>
                    {selectedCourse.description && selectedCourse.description.length > 150 && (
                      <button
                        type="button"
                        onClick={() => setDescExpanded(prev => !prev)}
                        className="mt-2 text-[12px] font-semibold text-foreground/55 hover:text-primary transition-colors uppercase tracking-[0.14em]"
                      >
                        {descExpanded ? 'See Less' : 'See More'}
                      </button>
                    )}
                  </motion.div>
                )}

                {/* Event/Information facts */}
                {isEventOrInfo && (
                  <motion.section
                    className="mt-8 pt-7 border-t border-border"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.32, ease: easing }}
                  >
                    <div className="flex items-center gap-2.5 mb-4">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                        {selectedCourse.type === 'information' ? 'Information Details' : 'Event Details'}
                      </span>
                    </div>
                    <dl className="space-y-3 text-[14px]">
                      {selectedCourse.type === 'event' && selectedCourse.date && (
                        <>
                          <div>
                            <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-1">When</dt>
                            <dd className="text-foreground/85">
                              {new Date(selectedCourse.date).toLocaleString('en-US', {
                                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true,
                                timeZone: selectedCourse.timezone || 'UTC'
                              })}
                            </dd>
                          </div>
                          {selectedCourse.timezone && (
                            <div>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-1">Timezone</dt>
                              <dd className="text-foreground/85">{selectedCourse.timezone.replace('_', ' ')}</dd>
                            </div>
                          )}
                        </>
                      )}
                      {selectedCourse.program && (
                        <div>
                          <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/55 mb-1">Program</dt>
                          <dd className="text-foreground/85">{selectedCourse.program.title}</dd>
                        </div>
                      )}
                    </dl>
                  </motion.section>
                )}

                {/* Course content — table of contents */}
                {!selectedCourse.type && !selectedCourse.date && (() => {
                  const courseContent = getCourseContent(selectedCourse);
                  const navigableContent = courseContent.filter(item => item.type !== 'reinforcement');
                  const totalContent = navigableContent.length;
                  const lessonsCount = courseContent.filter(item => item.type === 'lesson').length;
                  const submissionFormsCount = courseContent.filter(item => item.type === 'submissionForm').length;

                  return (
                    <motion.section
                      className="mt-8 pt-7 border-t border-border"
                      initial="hidden"
                      animate="visible"
                      variants={{ visible: { transition: { staggerChildren: 0.04, delayChildren: 0.35 } } }}
                    >
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
                          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                            Course Content
                          </span>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/45 tabular-nums">
                          Total: {totalContent} Items
                        </span>
                      </div>

                      {/* The list — hairline-separated, numbered */}
                      <ul className="border-t border-border">
                        {navigableContent.map((content, index) => {
                          const isCompleted = isContentCompleted(content, userProgress);
                          const isCurrent = userCurrentContent === content._id;
                          const canClick = isCompleted && onLessonClick;
                          const isLesson = content.type === 'lesson';

                          return (
                            <motion.li
                              key={content._id}
                              variants={{
                                hidden: { opacity: 0, y: 8 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easing } }
                              }}
                              className="border-b border-border"
                            >
                              <div
                                className={`flex items-center gap-4 py-3.5 px-1 transition-colors group ${
                                  canClick ? 'cursor-pointer hover:bg-foreground/[0.02]' : ''
                                }`}
                                onClick={() => canClick && onLessonClick(content)}
                              >
                                {/* Index */}
                                <span
                                  className={`flex-shrink-0 w-7 text-right font-bold tabular-nums text-[13px] tracking-tight ${
                                    isCurrent ? 'text-primary' :
                                    isCompleted ? 'text-foreground/40' :
                                    'text-foreground/55'
                                  }`}
                                >
                                  {String(index + 1).padStart(2, '0')}
                                </span>

                                {/* Title + type chip */}
                                <div className="flex-1 min-w-0 flex items-center gap-3">
                                  <span
                                    className={`text-[14px] sm:text-[15px] truncate ${
                                      isCompleted ? 'text-foreground/55' : 'text-foreground'
                                    } ${canClick ? 'group-hover:text-primary transition-colors' : ''}`}
                                  >
                                    {content.title}
                                  </span>
                                  <span
                                    className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.18em] ${
                                      isLesson ? 'text-primary/80' : 'text-mission/80'
                                    }`}
                                  >
                                    {isLesson ? 'Lesson' : 'Mission'}
                                  </span>
                                </div>

                                {/* Trailing actions / status */}
                                <div className="flex-shrink-0 flex items-center gap-3">
                                  {((isLesson && content.slug) || content.type === 'submissionForm') && (
                                    <a
                                      href={isLesson ? `/lesson/${content.slug}` : `/submissions/${content._id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-foreground/40 hover:text-primary transition-colors"
                                      title={isLesson ? 'Open lesson in new tab' : 'Open mission in new tab'}
                                    >
                                      <FiExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                  )}

                                  {isCurrent && !isCompleted && (
                                    <span className="flex items-center gap-1.5">
                                      <motion.span
                                        className="w-1.5 h-1.5 rounded-full bg-primary"
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                                      />
                                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Now</span>
                                    </span>
                                  )}

                                  {isCompleted && (
                                    <FaCheckCircle className="w-4 h-4 text-primary/80" aria-label="Completed" />
                                  )}

                                  {!isCurrent && !isCompleted && (
                                    <FaCircle className="w-2.5 h-2.5 text-foreground/15" aria-label="Upcoming" />
                                  )}
                                </div>
                              </div>
                            </motion.li>
                          );
                        })}
                      </ul>

                      {/* Summary footer */}
                      <div className="mt-5 flex items-center justify-between text-[11px] tabular-nums tracking-[0.14em] uppercase text-foreground/55 font-bold">
                        <div className="flex items-center gap-5">
                          <span className="flex items-center gap-1.5">
                            <FaBookOpen className="w-3 h-3" />
                            {lessonsCount} lessons
                          </span>
                          {submissionFormsCount > 0 && (
                            <span className="flex items-center gap-1.5">
                              <FaClipboardList className="w-3 h-3" />
                              {submissionFormsCount} missions
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.section>
                  );
                })()}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CourseDetailModal;
